import React, {
  useState,
  useCallback,
  useEffect,
  useContext,
  Fragment,
  SyntheticEvent,
} from "react";
import { Theme, makeStyles } from "@material-ui/core/styles";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  TablePagination,
  Grid,
  Modal,
  CardActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import { Clear } from "@material-ui/icons";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import "moment/locale/th";
import moment from "moment";
import MomentUtils from "@date-io/moment";
import useCsvDownloader from "use-csv-downloader";
import { useSnackbar } from "notistack";
import { IProject } from "../interface/project";
import clsx from "clsx";
import { LangContext } from "../context/lang";
import { ExportExcel } from "../material/icons";
import {
  getProjectPagination,
  getProjectCount,
  exportProjectToExcel,
} from "../apis/project";
moment.locale("th");

const useStyle = makeStyles((theme: Theme) => ({
  header: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    borderBottom: `solid 1px ${theme.palette.divider}`,
    "& .MuiCardHeader-action": {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      height: theme.spacing(4.5),
    },
  },
  headerTitle: {},
  cardHaft: { height: "100%" },
  textSearch: {
    margin: 0,
    "& .MuiInputBase-root": {
      borderRadius: `${theme.spacing(5)}px !important`,
    },
    "& .MuiFilledInput-underline:before": { borderBottom: "unset !important" },
    "& .MuiFilledInput-underline:after": { borderBottom: "unset !important" },
    "& input": {
      padding: `${theme.spacing(1) / 2}px ${theme.spacing(2)}px`,
    },
  },
  cardContent: {
    height: `calc(100% - 97px)`,
    padding: 0,
    "&:last-child": { paddingBottom: 0 },
  },
  tableContainer: {
    height: "100%",
  },
  inProcess: { color: theme.palette.primary.main },
  complete: {
    color: theme.palette.success.main,
  },
  actionHeader: {
    flexDirection: "row!important" as "row",
  },
  middle: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  clearIcon: { color: theme.palette.error.main },
  modalExport: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  modalBtn: { textTransform: "unset" },
}));

interface IColumn {
  id: "work_order_no" | "created_at" | "status";
  label: string;
  minWidth?: number;
  maxWidth?: number;
  align?: "right" | "center";
  format?: (value: number) => string;
}

const ProjectTable = (props: { department: string | number }) => {
  const classes = useStyle();
  const { department } = props;
  const downloadCsv = useCsvDownloader();
  const [open, setopen] = useState<boolean>(false);
  const [tableData, settableData] = useState<any[]>([]);
  const [search, setsearch] = useState<string>("");
  const [searchText, setsearchText] = useState<string>("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [count, setcount] = useState<number>(0);

  const [startDate, setstartDate] = React.useState<any>(new Date());
  const [endDate, setendDate] = React.useState<any>(new Date());

  const [rangesDate, setrangesDate] = useState<
    "custom" | "month" | "quarter" | "year" | String
  >("custom");

  const [loading, setloading] = useState<boolean>(false);
  const { lang } = useContext(LangContext);

  const { enqueueSnackbar } = useSnackbar();

  const columns: IColumn[] = [
    { id: "work_order_no", label: lang.data.workOrderNo, minWidth: 60 },
    {
      id: "created_at",
      label: lang.data.createDate,
      align: "center",
      minWidth: 100,
    },
    {
      id: "status",
      label: lang.data.status,
      minWidth: 100,
      align: "center",
    },
  ];

  const handleChangeRangesDate = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setrangesDate(event.target.value as String);
    if (event.target.value === "custom") {
      setstartDate(moment());
      setendDate(moment());
    } else if (event.target.value === "month") {
      setstartDate(
        moment(`${moment().format("M")}/01/${moment().format("YYYY")}`)
      );
      setendDate(
        moment(`${moment().format("M")}/01/${moment().format("YYYY")}`)
          .add(1, "month")
          .subtract(1, "day")
      );
    } else if (event.target.value === "quarter") {
      setstartDate(moment(`01/01/${moment().format("YYYY")}`));
      setendDate(moment(`03/31/${moment().format("YYYY")}`));
    } else if (event.target.value === "year") {
      setstartDate(moment(`01/01/${moment().format("YYYY")}`));
      setendDate(moment(`12/31/${moment().format("YYYY")}`));
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const loadData = useCallback(
    async (abortController: AbortController) => {
      try {
        const projData = getProjectPagination(
          search,
          rowsPerPage,
          page * rowsPerPage,
          department,
          abortController
        );
        const projCount = getProjectCount(search, department, abortController);
        const allFetch = await Promise.all([projData, projCount]);
        if (allFetch[0] && allFetch[1]) {
          settableData(allFetch[0].data);
          setcount(allFetch[1].data[0].count);
        }
      } catch (error) {
        console.log(error);
      }
    },
    [department, search, rowsPerPage, page]
  );

  useEffect(() => {
    const abortController = new AbortController();
    loadData(abortController);
    return () => {
      abortController.abort();
    };
  }, [department, search, rowsPerPage, page]);

  const downloadExcel = async () => {
    setloading(true);
    const start = new Date(startDate).toISOString().split("T")[0];
    const end = new Date(endDate).toISOString().split("T")[0];
    const rsProjs = await exportProjectToExcel(start, end, department);
    console.log(rsProjs);
    if (rsProjs.data && rsProjs.data.length > 0) {
      const dataForExcel = rsProjs.data.map((row: any) => {
        const {
          bbox,
          _id,
          department_id,
          created_by,
          responsible_person,
          updated_by,
          imports,
          ...restData
        } = row;
        return {
          ...restData,
          created_at: moment(restData.created_at).format("DD/MM/YYYY"),
          updated_at: moment(restData.updated_at).format("DD/MM/YYYY"),
        };
      });
      downloadCsv(dataForExcel, `project_${start}_${end}.csv`);
    } else {
      enqueueSnackbar(lang.data.noData, { variant: "warning" });
    }
    setloading(false);
    setopen(false);
  };

  const _renderYear = () => {
    const years = [];
    const countFromStart = Number(moment().format("YYYY")) - 2020;
    const dateStart = moment().subtract(countFromStart, "y");
    const dateEnd = moment();
    while (dateEnd.diff(dateStart, "years") >= 0) {
      years.push({
        value: dateStart.format("YYYY"),
        th: dateStart.format("YYYY"),
        en: dateStart.format("YYYY"),
      });
      dateStart.add(1, "year");
    }
    return years.map((y: any) => (
      <MenuItem key={y.value} value={y.value}>
        {y[lang.locale]}
      </MenuItem>
    ));
  };

  const compare = (a: any, b: any) => {
    const bandA = a.value;
    const bandB = b.value;
    let comparison = 0;
    if (bandA > bandB) {
      comparison = 1;
    } else if (bandA < bandB) {
      comparison = -1;
    }
    return comparison;
  };

  const converValueQuater = (sDate: Date) => {
    const month = Number(moment(sDate).format("M"));
    switch (true) {
      case month <= 3:
        return 1;

      case month >= 4 && month <= 6:
        return 2;

      case month > 6 && month <= 9:
        return 3;

      default:
        return 4;
    }
  };

  const _renderMonths = () => {
    const months = [];
    const dateStart = moment();
    const dateEnd = moment().add(11, "month");
    while (dateEnd.diff(dateStart, "months") >= 0) {
      months.push({
        value: Number(dateStart.format("M")),
        th: dateStart.locale("th").format("MMMM"),
        en: dateStart.locale("en").format("MMMM"),
      });
      dateStart.add(1, "month");
    }
    return months.sort(compare).map((m: any) => (
      <MenuItem key={m.value} value={m.value}>
        {m[lang.locale]}
      </MenuItem>
    ));
  };

  const _renderOption = () => {
    switch (rangesDate) {
      case "month":
        return (
          <Grid item xs={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel id="month-select-label">
                    {lang.data.month}
                  </InputLabel>
                  <Select
                    labelId="month-select-label"
                    id="month-select"
                    value={Number(moment(startDate).format("M"))}
                    onChange={(
                      event: React.ChangeEvent<{ value: unknown }>
                    ) => {
                      const newMounth = event.target.value as number;
                      const start_date = moment(
                        `${newMounth}/01/${moment(startDate).format("YYYY")}`
                      );
                      const end_date = moment(start_date)
                        .add(1, "month")
                        .subtract(1, "day");
                      setstartDate(start_date);
                      setendDate(end_date);
                    }}
                  >
                    {_renderMonths()}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel id="year-select-label">
                    {lang.data.year}
                  </InputLabel>
                  <Select
                    labelId="year-select-label"
                    id="year-select"
                    value={moment(startDate).format("YYYY")}
                    onChange={(
                      event: React.ChangeEvent<{ value: unknown }>
                    ) => {
                      setstartDate(
                        moment(
                          `${moment(startDate).format("MM/DD")}/${
                            event.target.value as string
                          }`
                        )
                      );
                      setendDate(
                        moment(
                          `${moment(endDate).format("MM/DD")}/${
                            event.target.value as string
                          }`
                        )
                      );
                    }}
                  >
                    {_renderYear()}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        );
      case "quarter":
        return (
          <Grid item xs={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel id="quarter-select-label">
                    {lang.data.quarter}
                  </InputLabel>
                  <Select
                    labelId="quarter-select-label"
                    id="quarter-select"
                    value={converValueQuater(startDate)}
                    onChange={(
                      event: React.ChangeEvent<{ value: unknown }>
                    ) => {
                      switch (event.target.value) {
                        case 1:
                          setstartDate(
                            moment(`01/01/${moment(startDate).format("YYYY")}`)
                          );
                          setendDate(
                            moment(`03/31/${moment(startDate).format("YYYY")}`)
                          );
                          break;
                        case 2:
                          setstartDate(
                            moment(`04/01/${moment(startDate).format("YYYY")}`)
                          );
                          setendDate(
                            moment(`06/30/${moment(startDate).format("YYYY")}`)
                          );
                          break;
                        case 3:
                          setstartDate(
                            moment(`07/01/${moment(startDate).format("YYYY")}`)
                          );
                          setendDate(
                            moment(`09/30/${moment(startDate).format("YYYY")}`)
                          );
                          break;
                        default:
                          setstartDate(
                            moment(`10/01/${moment(startDate).format("YYYY")}`)
                          );
                          setendDate(
                            moment(`12/31/${moment(startDate).format("YYYY")}`)
                          );
                          break;
                      }
                    }}
                  >
                    <MenuItem value={1}>{`${lang.data.quarter} 1`}</MenuItem>
                    <MenuItem value={2}>{`${lang.data.quarter} 2`}</MenuItem>
                    <MenuItem value={3}>{`${lang.data.quarter} 3`}</MenuItem>
                    <MenuItem value={4}>{`${lang.data.quarter} 4`}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel id="year-select-label">
                    {lang.data.year}
                  </InputLabel>
                  <Select
                    labelId="year-select-label"
                    id="year-select"
                    value={moment(startDate).format("YYYY")}
                    onChange={(
                      event: React.ChangeEvent<{ value: unknown }>
                    ) => {
                      setstartDate(
                        moment(`01/01/${event.target.value as string}`)
                      );
                      setendDate(
                        moment(`03/31/${event.target.value as string}`)
                      );
                    }}
                  >
                    {_renderYear()}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        );
      case "year":
        return (
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="year-select-label">{lang.data.year}</InputLabel>
              <Select
                labelId="year-select-label"
                id="year-select"
                value={moment(startDate).format("YYYY")}
                onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                  setstartDate(moment(`01/01/${event.target.value as string}`));
                  setendDate(moment(`12/31/${event.target.value as string}`));
                }}
              >
                {_renderYear()}
              </Select>
            </FormControl>
          </Grid>
        );
      default:
        return <Grid item xs={6} />;
    }
  };

  return (
    <Fragment>
      <Modal
        open={open}
        onClose={() => {}}
        className={classes.modalExport}
        disableEnforceFocus={true}
        disableAutoFocus={true}
        disableRestoreFocus={true}
      >
        <MuiPickersUtilsProvider
          utils={MomentUtils}
          libInstance={moment}
          locale="th-TH"
        >
          <Card style={{ minWidth: 600 }}>
            <CardHeader
              title={
                <Typography variant="h6">{lang.data.projReport}</Typography>
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel id="ranges-date-select-label">
                      {lang.data.duration}
                    </InputLabel>
                    <Select
                      labelId="ranges-date-select-label"
                      id="ranges-date-select"
                      value={rangesDate}
                      onChange={handleChangeRangesDate}
                    >
                      <MenuItem value={"custom"}>{lang.data.custom}</MenuItem>
                      <MenuItem value={"month"}>{lang.data.monthly}</MenuItem>
                      <MenuItem value={"quarter"}>
                        {lang.data.quarterly}
                      </MenuItem>
                      <MenuItem value={"year"}>{lang.data.yearly}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {_renderOption()}
                <Grid item xs={6}>
                  <KeyboardDatePicker
                    variant="inline"
                    margin="normal"
                    fullWidth
                    label={lang.data.startDate}
                    labelFunc={(date: any) =>
                      moment(date).locale("th").format("L")
                    }
                    value={startDate}
                    onChange={(date: any) => {
                      setstartDate(date);
                    }}
                    maxDate={endDate}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                    disabled={rangesDate !== "custom"}
                  />
                </Grid>
                <Grid item xs={6}>
                  <KeyboardDatePicker
                    variant="inline"
                    margin="normal"
                    fullWidth
                    label={lang.data.endDate}
                    labelFunc={(date: any) =>
                      moment(date).locale("th").format("L")
                    }
                    minDate={startDate}
                    value={endDate}
                    onChange={(date: any) => {
                      setendDate(date);
                    }}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                    disabled={rangesDate !== "custom"}
                  />
                </Grid>
              </Grid>
            </CardContent>
            <CardActions style={{ justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setopen(false);
                }}
                className={classes.modalBtn}
                color="secondary"
                disabled={loading}
              >
                {lang.data.cancel}
              </Button>
              <Button
                className={classes.modalBtn}
                onClick={downloadExcel}
                color="primary"
                disabled={loading}
              >
                {lang.data.confirm}
              </Button>
            </CardActions>
          </Card>
        </MuiPickersUtilsProvider>
      </Modal>
      <Card className={classes.cardHaft}>
        <CardHeader
          title={
            <Typography variant="subtitle1" className={classes.headerTitle}>
              {lang.data.proj}
            </Typography>
          }
          classes={{ action: classes.actionHeader }}
          action={
            <Fragment>
              <div className={classes.middle}>
                <form
                  onSubmit={(e: SyntheticEvent) => {
                    e.preventDefault();
                    setsearch(searchText);
                    setPage(0);
                    // setsearchText();
                  }}
                >
                  <TextField
                    variant="filled"
                    margin="dense"
                    placeholder={lang.data.search}
                    className={classes.textSearch}
                    value={searchText}
                    onChange={(e) => {
                      setsearchText(e.target.value);
                    }}
                    InputProps={{
                      endAdornment:
                        searchText.length > 0 ? (
                          <IconButton
                            size="small"
                            style={{ padding: 0, fontSize: "1rem" }}
                            onClick={() => {
                              setsearch("");
                              setsearchText("");
                            }}
                          >
                            <Clear
                              className={classes.clearIcon}
                              style={{ width: "0.75em", height: "0.75em" }}
                            />
                          </IconButton>
                        ) : null,
                    }}
                  />
                </form>
              </div>
              <div className={classes.middle}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setopen(true);
                  }}
                >
                  <ExportExcel />
                </IconButton>
              </div>
            </Fragment>
          }
          className={classes.header}
        />
        <CardContent className={classes.cardContent}>
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader size="small" aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row: IProject) => {
                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} key={row._id}>
                      <TableCell>{row.work_order_no}</TableCell>
                      <TableCell align="center">
                        {new Date(String(row.created_at)).toLocaleDateString(
                          "th-TH",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          }
                        )}
                      </TableCell>
                      <TableCell
                        align={"center"}
                        className={clsx(
                          row.status === 2 && classes.complete,
                          row.status === 1 && classes.inProcess
                        )}
                      >
                        {row.status === 0
                          ? lang.data.waitting
                          : row.status === 1
                          ? lang.data.inProcess
                          : row.status === 2
                          ? lang.data.complete
                          : row.status}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Fragment>
  );
};

export default ProjectTable;
